<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <div class="cf-flex cf-flex--column cf-flex--gap-16">
      <cf-info-banner
        v-if="isGiftedPointsClaim"
        :message="giftedPointsClaimInfoContent"
        icon="cf-icon far fa-gift"
      />

      <cf-info-banner
        v-if="isPromoClaim"
        :message="promoClaimInfoContent"
        icon="cf-icon far fa-gift"
      />

      <img
        v-if="signInContent.imageUrl"
        class="cf-image cf-image--center cf-image--logo"
        :alt="merchant.name"
        :src="signInContent.imageUrl"
        onerror="this.src='/images/image-not-found.png';"
      >

      <h2
        v-if="signInContent.title"
        class="cf-title cf-title--1"
      >
        {{ signInContent.title }}
      </h2>

      <p
        v-if="sessionExpired"
        class="cf-subtitle cf-subtitle--2 cf-subtitle--black"
      >
        Your session has expired. Please sign in to continue.
      </p>

      <p
        v-else
        class="cf-subtitle cf-subtitle--2 cf-subtitle--black"
      >
        {{ signInContent.subtitle }}
      </p>
    </div>

    <div
      v-if="merchant.allowLoginByPhoneNumber"
      class="cf-tabs"
    >
      <button
        :class="['cf-tab', { 'cf-tab--active': isEmailSignIn }]"
        type="button"
        @click="setSignInMethodHandler('email')"
      >
        Email Address
      </button>

      <button
        :class="['cf-tab', { 'cf-tab--active': isPhoneNumberSignIn }]"
        type="button"
        @click="setSignInMethodHandler('phoneNumber')"
      >
        Phone Number
      </button>
    </div>

    <cf-validated-form
      name="signInForm"
      @valid-submit="signInFormSubmitHandler"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-16">
        <transition
          mode="out-in"
          :name="isPhoneNumberSignIn ? 'slide-to-right-fast' : 'slide-to-left-fast'"
        >
          <cf-phone-number-input
            v-if="isPhoneNumberSignIn"
            key="phone"
            v-model="phoneNumber"
            label="Phone Number"
            name="phoneNumberSignIn"
            :validations="{ required: true }"
          />

          <cf-text-input
            v-else
            key="email"
            v-model="email"
            type="email"
            label="Email"
            name="emailSignIn"
            :validations="{ required: true }"
          />
        </transition>

        <cf-text-input
          v-model="password"
          type="password"
          label="Password"
          name="passwordSignIn"
          :validations="{ required: true }"
        />

        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <button
            class="cf-link cf-link--brand-secondary cf-link--heavy"
            type="button"
            @click="openResetPasswordFormClickHandler"
          >
            Forgot your password?
          </button>

          <button
            :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': isSigningIn }]"
            type="submit"
          >
            {{ signInContent.buttonCta }}
          </button>
        </div>
      </div>
    </cf-validated-form>

    <cf-title-separator v-if="isThirdPartySignInEnabled || isSignInWithPasscodeEnabled" />

    <button
      v-if="isSignInWithPasscodeEnabled"
      class="cf-button cf-button--tertiary cf-button--lg cf-button--fill"
      type="button"
      @click="signInWithPasscodeClickHandler"
    >
      {{ signInContent.passcodeCta }}
    </button>

    <cf-third-party-authentication
      v-if="isThirdPartySignInEnabled"
      auth-type="sign-in"
    />

    <div class="cf-modal__footer">
      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--black">
        Don't have an account?

        <button
          class="cf-link cf-link--brand-secondary cf-link--heavy"
          type="button"
          @click="openSignUpModalHandler"
        >
          Create Account
        </button>
      </p>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfInfoBanner from '@/view/components/shared/cf-info-banner/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';

  import CfThirdPartyAuthentication from '@/view/components/globals/cf-third-party-authentication/index.vue';


  export default {
    name: 'SignIn',

    components: {
      CfValidatedForm,
      CfInfoBanner,
      CfPhoneNumberInput,
      CfTextInput,
      CfTitleSeparator,
      CfThirdPartyAuthentication
    },

    props: {
      sessionExpired: {
        type: Boolean,
        default: false
      },

      onSuccess: {
        type: Function,
        default: null
      }
    },

    data() {
      return {
        email: null,
        isSigningIn: false,
        password: null,
        phoneNumber: null,
        signInMethod: 'email'
      };
    },

    computed: {
      ...mapGetters('content', ['dynamicContent']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['currentUser', 'session']),
      ...mapGetters('offers', ['offers']),

      signInContent() {
        const { metadata, title } = this.dynamicContent.SignIn;

        const { imageUrl, subtitle, buttonCta, signInEmailCode, signInTextCode } = metadata;

        let passcodeCta;

        if (this.isEmailSignIn) passcodeCta = signInEmailCode;

        if (this.isPhoneNumberSignIn) passcodeCta = signInTextCode;

        return { title, imageUrl, subtitle, buttonCta, passcodeCta };
      },

      isSignInWithPasscodeEnabled() {
        return this.merchant.features['sign-in-with-passcode'];
      },

      isThirdPartySignInEnabled() {
        return this.merchant.thirdPartySignIn.enabled;
      },

      isGiftedPointsClaim() {
        return !!this.offers.giftingPoints.pointGiftClaimCode;
      },

      giftedPointsClaimInfoContent() {
        const pointsCurrency = this.offers.giftingPoints.currencyPluralName;

        return `Sign in to claim your ${pointsCurrency}!`;
      },

      resetTop() {
        return !this.signInContent.imageUrl.trim();
      },

      isPromoClaim() {
        return !!this.offers.promoCode.claimCode;
      },

      promoClaimInfoContent() {
        return 'Sign in to claim your reward!';
      },

      isPhoneNumberSignIn() {
        return this.signInMethod === 'phoneNumber';
      },

      isEmailSignIn() {
        return this.signInMethod === 'email';
      },

      contactInfo() {
        return this.isPhoneNumberSignIn ? this.phoneNumber : this.email;
      }
    },

    mounted() {
      if (this.offers.giftingPoints.pointGiftClaimCode) {
        this.$store.commit('offers/SET_GIFTING_POINTS_DATA', { claimGiftedPointsFlow: 'SIGNED_IN' });
      }
    },

    methods: {
      setSignInMethodHandler(method) {
        this.signInMethod = method;
      },

      redirect(route) {
        const routerBody = { name: route.name, params: route.params, query: route.query };

        this.$router.replace(routerBody);
      },

      signInWithPasscodeClickHandler() {
        this.$openModal('signInWithPasscodeModal', { signInMethod: this.signInMethod });
      },

      async signInFormSubmitHandler() {
        this.isSigningIn = true;

        const dispatchBody = {
          phoneNumberOrEmail: this.contactInfo,
          password: this.password
        };

        const { error } = await this.$store.dispatch('session/signIn', dispatchBody);

        if (error) {
          this.handleError(error);
        }
        else {
          await this.handleSuccess();
        }

        this.isSigningIn = false;
      },

      async handleSuccess() {
        const { signInRedirectRoute } = this.session;

        await this.checkForRetroClaims();

        if (this.onSuccess) await this.onSuccess();
        else if (signInRedirectRoute) this.redirect(signInRedirectRoute);

        this.$closeModal('signIn');

        // this.$triggerGoogleTrackingEvent('SignInSuccess', 'Account');

        this.$triggerNewRelicEvent('SignInSuccess', 'Information', 'Sign In Success');
      },

      async checkForRetroClaims() {
        const { receiptToken } = this.$route.params;

        const retroClaimReceipt = this.offers.retroReceiptToken;

        if (receiptToken || retroClaimReceipt) {
          const dispatchBody = { type: 'cfReceiptToken', value: receiptToken || retroClaimReceipt };

          await this.$store.dispatch('offers/claimPreviousOrder', dispatchBody);
        }
      },

      handleError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, this.email, this.phoneNumber);

        if (requiresAuth) {
          const modalBody = { ...rest, onSuccess: this.handleSuccess };

          this.$openModal('twoFactorAuthModal', modalBody, { isRequired: true });
        }

        else {
          errorHandlers.generic(error);

          // this.$triggerGoogleTrackingEvent('SignInFailure', 'Account');

          this.$triggerNewRelicEvent('SignInFailure', 'Error', 'Sign In Failure');
        }
      },

      openResetPasswordFormClickHandler() {
        this.$closeModal('signIn');

        this.$openModal('reset-password-modal');
      },

      openSignUpModalHandler() {
        this.$openModal('createAccount');

        this.$closeModal('signIn');
      }
    }
  };
</script>
